import { SignInIdentifier } from '@logto/schemas';
import { parsePhoneNumberFromString } from 'libphonenumber-js/min';

import { resolveLoginIdentifier, type LoginIdentifierResult } from './login-identifier';

export type ContextualLoginIdentifier =
  | Exclude<LoginIdentifierResult, { ok: true }>
  | (Extract<LoginIdentifierResult, { ok: true }> & { region?: string });

const domestic = /^1[3-9]\d{9}$/;
const invalid = (): ContextualLoginIdentifier => ({ ok: false, error: 'identifier_invalid' });

/** Format only. An unsupported SMS region may still use an existing password. */
export function resolveContextualLoginIdentifier(
  input: string,
  mode: 'phone' | 'password'
): ContextualLoginIdentifier {
  const value = input.trim();
  if (!value) {
    return invalid();
  }
  if (mode === 'password') {
    if (value.startsWith('@')) {
      return resolveLoginIdentifier(value, SignInIdentifier.Username);
    }
    if (!value.startsWith('+') && value.includes('@')) {
      return resolveLoginIdentifier(value, SignInIdentifier.Email);
    }
  }

  // Accept familiar phone separators, never letters, extensions or Unicode digits.
  const compact = value.replace(/[ ()-]/g, '');
  if (value.startsWith('+') || domestic.test(compact) || mode === 'phone') {
    if (!/^[\d ()+-]+$/.test(value)) {
      return invalid();
    }
    if (!compact.startsWith('+') && !domestic.test(compact)) {
      return invalid();
    }
    if (compact.startsWith('+86') && !domestic.test(compact.slice(3))) {
      return invalid();
    }
    const phone = parsePhoneNumberFromString(compact.startsWith('+') ? compact : '+86' + compact, {
      extract: false,
    });
    if (!phone?.isValid()) {
      return invalid();
    }
    return {
      ok: true,
      type: SignInIdentifier.Phone,
      canonical: phone.number,
      provider: phone.number.slice(1),
      region: phone.country,
    };
  }
  return resolveLoginIdentifier(value, SignInIdentifier.Username);
}

export function canSendContextualSms(
  identifier: ContextualLoginIdentifier,
  smsRegions: readonly string[]
): boolean {
  return (
    identifier.ok &&
    identifier.type === SignInIdentifier.Phone &&
    identifier.region !== undefined &&
    smsRegions.includes(identifier.region)
  );
}
